import { toast } from 'sonner';
import { useCallback } from 'react';
import { useMutation } from '@apollo/client/react';

import { StorageSettings } from 'src/containers/SoftwareEscrow/Technical';
import { ClientError, getApiError } from 'src/utils/errors';
import { type ISoftwareEscrowUpdateParams } from 'src/graphql/schema';
import { SoftwareEscrowUpdateDocument } from 'src/graphql/actions/softwareEscrow/softwareEscrowUpdate.mutation';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { SoftwareOnboardingMain } from 'src/components/SoftwareOnboarding';

const StorageSettingsPage = () => {
  const {
    softwareEscrow: { id: softwareEscrowId, storageRegion, assetVersionsLimit },
  } = useSoftwareEscrow();
  const [updateDepositSettings] = useMutation(SoftwareEscrowUpdateDocument);

  const onSettingsChange = useCallback(
    async (params: ISoftwareEscrowUpdateParams) => {
      try {
        const { data } = await updateDepositSettings({
          variables: {
            softwareEscrowId,
            softwareEscrowParams: params,
          },
        });

        if (data?.softwareEscrowUpdate?.success) {
          toast.success('Saved!');
        } else {
          throw new ClientError(data?.softwareEscrowUpdate?.errors?.[0]);
        }
      } catch (e: unknown) {
        getApiError(e, toast.error);
      }
    },
    [updateDepositSettings, softwareEscrowId],
  );

  const onStorageRegionChange = (storageRegion: string) => onSettingsChange({ storageRegion });

  const onAssetVersionsLimitChange = (value: string) =>
    onSettingsChange({ assetVersionsLimit: value === 'full' ? null : Number(value) });

  return (
    <SoftwareOnboardingMain
      subtitle="Technical information:"
      title="Storage settings"
      description="Configure the settings related to the deposited assets, and their storage in escrow."
      noteText="You can edit this later under Settings → Technical Information."
    >
      <div className="container mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <StorageSettings
            storageRegion={storageRegion ?? ''}
            assetVersionsLimit={assetVersionsLimit ?? undefined}
            onStorageRegionChange={onStorageRegionChange}
            onAssetVersionsLimitChange={onAssetVersionsLimitChange}
          />
        </div>
      </div>
    </SoftwareOnboardingMain>
  );
};

export default StorageSettingsPage;
